/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';
import {faIdCard} from '@fortawesome/free-solid-svg-icons';

import {
  Alert,
  ContentBody,
  FieldWrapper,
  NxButton,
  NxLoadWrapper,
  NxSubmitMask,
  NxTooltip,
  Page,
  PageHeader,
  PageTitle,
  Section,
  SectionFooter,
  Select,
  Textarea,
  Textfield,
  Utils
} from 'nexus-ui-plugin';

import SamlConfigurationMachine from './SamlConfigurationMachine';
import UIStrings from '../../../../constants/UIStrings';

import './SamlConfiguration.scss';

const FIELDS = UIStrings.SAML_CONFIGURATION.FIELDS;

export default function SamlConfiguration() {
  const [current, send] = useMachine(SamlConfigurationMachine, {devTools: true});

  const {data : settings, isPristine, validationErrors, saveError, loadError} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = Utils.isInvalid(validationErrors);
  const errorMessage = saveError;
  const hasData = settings && settings !== {};

  function updateSettings({target}) {
    send('UPDATE', {
      data: {
        [target.name]: target.value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('RESET');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  function retry() {
    send('RETRY');
  }

  let error = null;
  if (errorMessage instanceof Array) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}
      <ul>
        {errorMessage.map(error => <li key={error.id}>{JSON.stringify(error)}</li>)}
      </ul>
    </Alert>;
  }
  else if (errorMessage) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}<br/>
      {errorMessage}
    </Alert>;
  }

  return <Page>
    <PageHeader><PageTitle icon={faIdCard} {...UIStrings.SAML_CONFIGURATION.MENU}/></PageHeader>
    <ContentBody className='nxrm-saml-configuration'>
      <Section>
      <NxLoadWrapper loading={isLoading} error={loadError ? `${loadError}` : null} retryHandler={retry}>
        {hasData && <>
          {isSaving && <NxSubmitMask message={UIStrings.SAVING}/>}
          {error}

          <FieldWrapper labelText={FIELDS.idpMetadataLabel}>
            <Textarea
                className="nx-text-input--long"
                {...Utils.fieldProps('idpMetadata', current)}
                onChange={updateSettings}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.entityIdUriLabel} descriptionText={FIELDS.entityIdUriDescription}>
            <Textfield
                className="nx-text-input--long"
                {...Utils.fieldProps('entityIdUri', current)}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.validateResponseSignatureLabel} descriptionText={FIELDS.validateResponseSignatureDescription}>
            <Select name="validateResponseSignature" value={settings.validateResponseSignature} onChange={updateSettings}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.validateAssertionSignatureLabel} descriptionText={FIELDS.validateAssertionSignatureDescription}>
            <Select name="validateAssertionSignature" value={settings.validateAssertionSignature} onChange={updateSettings}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FieldWrapper>

          <fieldset>
            <legend>IdP Field Mappings</legend>

            <FieldWrapper labelText={FIELDS.usernameAttrLabel}>
              <Textfield
                  {...Utils.fieldProps('usernameAttr', current)}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.firstNameAttrLabel} isOptional>
              <Textfield
                  {...Utils.fieldProps('firstNameAttr', current)}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.lastNameAttrLabel} isOptional>
              <Textfield
                  {...Utils.fieldProps('lastNameAttr', current)}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.emailAttrLabel} isOptional>
              <Textfield
                  {...Utils.fieldProps('emailAttr', current)}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper fieldName="roleAttr" labelText={FIELDS.roleAttrLabel} isOptional>
              <Textfield
                  {...Utils.fieldProps('roleAttr', current)}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>
          </fieldset>

          <SectionFooter>
            <NxTooltip title={Utils.saveTooltip({isPristine, isInvalid})}>
              <NxButton variant="primary" className={(isPristine || isInvalid) && 'disabled'} onClick={saveSettings} type="submit">
                {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
            <NxTooltip title={Utils.discardTooltip({isPristine})}>
              <NxButton className={isPristine && 'disabled'} onClick={discardChanges}>
                {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
          </SectionFooter>
        </>}
        </NxLoadWrapper>
      </Section>
    </ContentBody>
  </Page>;
}
